import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type {PanelDataSource} from '../data/PanelDataSource.js';

/**
 * Um pedido de reautenticação pendente.
 *
 * Nasce quando uma escrita volta com "confirme o passkey" (403 de
 * reautenticação). O diálogo roda a cerimônia e, no sucesso, só reexecuta a
 * ação se o usuário mandar: entre a recusa e a confirmação passou tempo, e
 * repetir sozinho uma ação que o dono talvez já não queira é decidir por ele.
 */
export interface PedidoDeReauth {
  /** O que a tela diz sobre a ação recusada. Mensagem nossa, nunca da API. */
  message: string;
  /** Reexecuta a ação recusada. `null` quando não há o que repetir. */
  retry: (() => void) | null;
}

export interface ReauthContextValue {
  /**
   * `true` só quando a fonte de dados tem porta de autenticação. Desarmado
   * (fixture e dublês de teste), `oferecer` é inerte e o painel se comporta
   * exatamente como antes desta tela existir.
   */
  armado: boolean;
  pedido: PedidoDeReauth | null;
  oferecer: (pedido: PedidoDeReauth) => void;
  fechar: () => void;
}

/**
 * Default desarmado, e não `null` com hook que lança: os testes antigos
 * renderizam pedaços do painel sem este provider, e quebrar todos eles por
 * causa de um recurso que nem existe no modo deles seria o rabo abanando o
 * cachorro.
 */
const ReauthContext = createContext<ReauthContextValue>({
  armado: false,
  pedido: null,
  oferecer: () => undefined,
  fechar: () => undefined,
});

export const useReauth = (): ReauthContextValue => useContext(ReauthContext);

export const ReauthProvider = ({
  dataSource,
  children,
}: {
  dataSource: PanelDataSource;
  children: ReactNode;
}) => {
  const armado = dataSource.auth !== null;
  const [pedido, setPedido] = useState<PedidoDeReauth | null>(null);

  const oferecer = useCallback(
    (novo: PedidoDeReauth) => {
      // Sem porta não há cerimônia possível: oferecer viraria um diálogo com
      // um botão que não faz nada. O chamador consulta `armado` antes, mas a
      // cerca vale mesmo se ele esquecer.
      if (armado) {
        setPedido(novo);
      }
    },
    [armado],
  );

  const fechar = useCallback(() => {
    setPedido(null);
  }, []);

  const value = useMemo<ReauthContextValue>(
    () => ({armado, pedido, oferecer, fechar}),
    [armado, fechar, oferecer, pedido],
  );

  return (
    <ReauthContext.Provider value={value}>{children}</ReauthContext.Provider>
  );
};
